import React, { useState } from 'react';
import { ChevronLeft, User, Bell, HelpCircle, ChevronRight, LogOut, UserX, Send, FileText, Shield, Users } from 'react-feather';
import { UserSettings } from '../types';
import './SettingsView.css';

interface SettingsViewProps {
  settings: UserSettings;
  onSettingsChange: (settings: UserSettings) => void;
  onSignOut?: () => void;
  isAdmin?: boolean;
  onAdminClick?: () => void;
}

export const SettingsView: React.FC<SettingsViewProps> = ({ 
  settings, 
  onSettingsChange,
  onSignOut,
  isAdmin,
  onAdminClick
}) => {
  const [currentSection, setCurrentSection] = useState<'main' | 'notifications' | 'account' | 'blocked-users' | 'help-support'>('main');

  const handleNotificationToggle = (type: 'push' | 'email') => {
    onSettingsChange({
      ...settings,
      [type === 'push' ? 'pushNotifications' : 'emailNotifications']: 
        !settings[type === 'push' ? 'pushNotifications' : 'emailNotifications']
    });
  };

  const handleUnblockUser = (username: string) => {
    onSettingsChange({
      ...settings,
      blockedUsers: settings.blockedUsers.filter(user => user !== username)
    });
  };

  // Help & Support form state
  const [supportForm, setSupportForm] = useState({
    subject: '',
    message: '',
    email: '',
    isSubmitting: false,
    isSubmitted: false
  });

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSupportForm(prev => ({ ...prev, isSubmitting: true }));
    
    // Simulate form submission
    setTimeout(() => {
      setSupportForm(prev => ({ 
        ...prev, 
        isSubmitting: false, 
        isSubmitted: true,
        subject: '',
        message: '',
        email: ''
      }));
      
      // Reset submitted state after 3 seconds
      setTimeout(() => {
        setSupportForm(prev => ({ ...prev, isSubmitted: false }));
      }, 3000);
    }, 1000);
  };

  const renderHelpSupportSettings = () => (
    <div className="settings-page">
      <div className="settings-header">
        <button className="back-button-floating" onClick={() => setCurrentSection('main')}>
          <ChevronLeft size={20} />
        </button>
        <div className="settings-header-content">
          <h1 className="settings-title">Help & Support</h1>
        </div>
      </div>

      <div className="settings-content">
        {/* Contact Form */}
        <div className="settings-group">
          <div className="section-title">Send us a message</div>
          
          {supportForm.isSubmitted ? (
            <div className="form-success">
              <div className="success-icon">✓</div>
              <h3>Message sent successfully!</h3>
              <p>We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSupportSubmit} className="support-form">
              <div className="form-group">
                <label htmlFor="email">Your Email</label>
                <input
                  type="email"
                  id="email"
                  value={supportForm.email}
                  onChange={(e) => setSupportForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  id="subject"
                  value={supportForm.subject}
                  onChange={(e) => setSupportForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="How can we help you?"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  value={supportForm.message}
                  onChange={(e) => setSupportForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Please describe your issue or question..."
                  rows={4}
                  required
                />
              </div>

              <button 
                type="submit" 
                className="submit-button"
                disabled={supportForm.isSubmitting}
              >
                <Send size={16} />
                {supportForm.isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}
        </div>

        {/* Legal Links */}
        <div className="settings-group">
          <div className="section-title">Legal</div>
          
          <button className="setting-item" onClick={() => window.open('#', '_blank')}>
            <div className="setting-icon">
              <FileText size={20} />
            </div>
            <div className="setting-info">
              <div className="setting-name">Terms & Conditions</div>
              <div className="setting-description">View our terms of service</div>
            </div>
            <ChevronRight size={16} />
          </button>

          <button className="setting-item" onClick={() => window.open('#', '_blank')}>
            <div className="setting-icon">
              <Shield size={20} />
            </div>
            <div className="setting-info">
              <div className="setting-name">Privacy Policy</div>
              <div className="setting-description">How we handle your data</div>
            </div>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  const renderBlockedUsersSettings = () => (
    <div className="settings-page">
      <div className="settings-header">
        <button className="back-button-floating" onClick={() => setCurrentSection('main')}>
          <ChevronLeft size={20} />
        </button>
        <div className="settings-header-content">
          <h1 className="settings-title">Blocked Users</h1>
        </div>
      </div>

      <div className="settings-content">
        {settings.blockedUsers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <UserX size={48} color="#9ca3af" />
            </div>
            <h3>No blocked users</h3>
            <p>Users you block will have their submissions hidden from your filtered view.</p>
          </div>
        ) : (
          <>
            <div className="settings-description">
              <p>These users are currently blocked. Their submissions won't appear in your filtered view.</p>
            </div>
            
            <div className="blocked-users-list">
              {settings.blockedUsers.map((username) => (
                <div key={username} className="blocked-user-item">
                  <div className="blocked-user-icon">
                    <UserX size={16} />
                  </div>
                  <div className="blocked-user-content">
                    <h3 className="setting-name">{username}</h3>
                  </div>
                  <button 
                    className="unblock-button"
                    onClick={() => handleUnblockUser(username)}
                  >
                    Unblock
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-page">
      <div className="settings-header">
        <button className="back-button-floating" onClick={() => setCurrentSection('main')}>
          <ChevronLeft size={20} />
        </button>
        <div className="settings-header-content">
          <h1 className="settings-title">Notifications</h1>
        </div>
      </div>

      <div className="settings-content">
        <div className="settings-group">
          <div className="setting-item">
            <div className="setting-info">
              <h3 className="setting-name">Push Notifications</h3>
              <p className="setting-description">Get notified about incidents in your alert zones</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.pushNotifications}
                onChange={() => handleNotificationToggle('push')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <h3 className="setting-name">Email Notifications</h3>
              <p className="setting-description">Receive incident summaries via email</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={() => handleNotificationToggle('email')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAccountSettings = () => (
    <div className="settings-page">
      <div className="settings-header">
        <button className="back-button-floating" onClick={() => setCurrentSection('main')}>
          <ChevronLeft size={20} />
        </button>
        <div className="settings-header-content">
          <h1 className="settings-title">Account</h1>
        </div>
      </div>

      <div className="settings-content">
        <div className="profile-section">
          <div className="profile-avatar">
            <User size={24} />
          </div>
          <div className="profile-info">
            <h3>John Doe</h3>
            <p>john.doe@example.com</p>
          </div>
        </div>

        <div className="settings-group">
          <button className="setting-item clickable">
            <div className="setting-info">
              <h3 className="setting-name">Privacy & Security</h3>
              <p className="setting-description">Manage your data and privacy settings</p>
            </div>
            <ChevronRight size={16} />
          </button>

          <button className="setting-item clickable">
            <div className="setting-info">
              <h3 className="setting-name">Help & Support</h3>
              <p className="setting-description">Get help and contact support</p>
            </div>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  const renderMainSettings = () => (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">
            Manage your account and notification preferences
          </p>
        </div>
      </div>

      <div className="settings-content">
        <div className="settings-group">
          <button 
            className="setting-item clickable"
            onClick={() => setCurrentSection('account')}
          >
            <div className="setting-icon">
              <User size={20} />
            </div>
            <div className="setting-info">
              <h3 className="setting-name">Account</h3>
              <p className="setting-description">Profile and account management</p>
            </div>
            <ChevronRight size={16} />
          </button>

          <button 
            className="setting-item clickable"
            onClick={() => setCurrentSection('notifications')}
          >
            <div className="setting-icon">
              <Bell size={20} />
            </div>
            <div className="setting-info">
              <h3 className="setting-name">Notifications</h3>
              <p className="setting-description">Configure alert preferences</p>
            </div>
            <ChevronRight size={16} />
          </button>

          <button 
            className="setting-item clickable"
            onClick={() => setCurrentSection('blocked-users')}
          >
            <div className="setting-icon">
              <UserX size={20} />
            </div>
            <div className="setting-info">
              <h3 className="setting-name">Blocked Users</h3>
              <p className="setting-description">{settings.blockedUsers.length} user{settings.blockedUsers.length !== 1 ? 's' : ''} blocked</p>
            </div>
            <ChevronRight size={16} />
          </button>

          <button 
            className="setting-item clickable"
            onClick={() => setCurrentSection('help-support')}
          >
            <div className="setting-icon">
              <HelpCircle size={20} />
            </div>
            <div className="setting-info">
              <h3 className="setting-name">Help & Support</h3>
              <p className="setting-description">FAQs and contact information</p>
            </div>
            <ChevronRight size={16} />
          </button>
        </div>

        {isAdmin && (
          <div className="settings-group admin-section">
            <button 
              className="setting-item clickable admin-item"
              onClick={onAdminClick}
            >
              <div className="setting-icon admin-icon">
                <Users size={20} />
              </div>
              <div className="setting-info">
                <h3 className="setting-name">Admin Dashboard</h3>
                <p className="setting-description">Manage users and approvals</p>
              </div>
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        <div className="settings-group sign-out-section">
          <button 
            className="setting-item clickable sign-out-button"
            onClick={onSignOut}
          >
            <div className="setting-icon sign-out-icon">
              <LogOut size={20} />
            </div>
            <div className="setting-info">
              <h3 className="setting-name">Sign Out</h3>
              <p className="setting-description">Sign out of your account</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  switch (currentSection) {
    case 'notifications':
      return renderNotificationSettings();
    case 'account':
      return renderAccountSettings();
    case 'blocked-users':
      return renderBlockedUsersSettings();
    case 'help-support':
      return renderHelpSupportSettings();
    default:
      return renderMainSettings();
  }
}; 